// SyllabusPanel — chapters per class + subject, ticked off as they're taught,
// with % completed per subject and a school-wide overview.
import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { getClasses, getSyllabus, getSyllabusProgress, addSyllabus, updateSyllabusTopic, deleteSyllabusTopic } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, errText, inputCls, ClassSelect } from '../ui/Form';
import { fmtDate, fmtMonth } from '../ui/format';

export default function SyllabusPanel() {
  const [classes,  setClasses]  = useState([]);
  const [classId,  setClassId]  = useState('');
  const [subjects, setSubjects] = useState([]);
  const [progress, setProgress] = useState([]);
  const [form,     setForm]     = useState({ subject: '', bulk: '', planned_month: '' });
  const [msg,      setMsg]      = useState('');

  useEffect(() => {
    getClasses().then(cs => { setClasses(cs); if (cs.length) setClassId(String(cs[0].id)); }).catch(err => setMsg(errText(err)));
    getSyllabusProgress().then(setProgress).catch(() => {});
  }, []);
  useEffect(() => { if (classId) load(); }, [classId]);

  async function load() {
    try {
      setSubjects(await getSyllabus(classId));
      setProgress(await getSyllabusProgress());
    } catch (err) { setMsg(errText(err)); }
  }

  async function add(e) {
    e.preventDefault();
    setMsg('');
    try {
      const r = await addSyllabus({ class_id: Number(classId), subject: form.subject.trim(), bulk: form.bulk, planned_month: form.planned_month || null });
      setMsg(`Added ${r.length} chapter${r.length === 1 ? '' : 's'} ✅`);
      setForm({ ...form, bulk: '' });
      load();
    } catch (err) { setMsg(errText(err)); }
  }

  async function toggle(t) {
    try { await updateSyllabusTopic(t.id, { completed: !t.completed }); load(); } catch (err) { setMsg(errText(err)); }
  }

  async function remove(t) {
    if (!window.confirm(`Remove "${t.chapter}"?`)) return;
    try { await deleteSyllabusTopic(t.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  const byClass = {};
  for (const p of progress) (byClass[p.class_name] = byClass[p.class_name] || []).push(p);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Syllabus</h2>
        <ClassSelect classes={classes} value={classId} onChange={setClassId} />
      </div>
      <Msg msg={msg} className="mb-3" />

      <div className="grid grid-cols-[1fr_minmax(280px,360px)] gap-5 items-start max-[1000px]:grid-cols-1">
        <div className="flex flex-col gap-4">
          {subjects.map(s => (
            <Card key={s.subject}>
              <CardHeader title={s.subject} action={
                <span className="text-sm font-bold text-transit-deep tabular-nums">{s.done}/{s.total} · {s.percent}%</span>
              } />
              <div className="h-1.5 bg-line-soft"><div className="h-full bg-transit" style={{ width: `${s.percent}%` }} /></div>
              {s.topics.map((t, i) => (
                <div key={t.id} className="flex items-center gap-3 px-5 py-2.5 border-t border-line-soft first:border-t-0">
                  <input type="checkbox" checked={t.completed} onChange={() => toggle(t)} className="w-4 h-4 accent-[var(--transit)]" aria-label={`Mark ${t.chapter} done`} />
                  <span className={`flex-1 text-sm ${t.completed ? 'text-ink-soft line-through' : 'text-ink font-semibold'}`}>
                    {i + 1}. {t.chapter}{t.topic ? <span className="font-normal text-ink-soft"> — {t.topic}</span> : null}
                  </span>
                  {t.planned_month && <span className="text-xs text-ink-soft">Plan: {fmtMonth(t.planned_month)}</span>}
                  {t.completed_on && <span className="text-xs font-bold text-success">Done {fmtDate(t.completed_on)}</span>}
                  <button onClick={() => remove(t)} aria-label="Remove chapter" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </Card>
          ))}
          {!subjects.length && <Card className="p-6 text-sm text-ink-soft">No syllabus for this class yet — add chapters on the right.</Card>}
        </div>

        <div className="flex flex-col gap-4">
          <Card className="p-5">
            <h3 className="font-display font-bold text-[15px] text-ink mb-3">Add chapters</h3>
            <form onSubmit={add} className="flex flex-col gap-3">
              <Field label="Subject">
                <input required list="syllabus-subjects" className={inputCls} placeholder="e.g. Science" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                <datalist id="syllabus-subjects">{subjects.map(s => <option key={s.subject} value={s.subject} />)}</datalist>
              </Field>
              <Field label="Chapters — one per line">
                <textarea required rows={6} className={inputCls} placeholder={'Food: where does it come from?\nComponents of food\nFibre to fabric'} value={form.bulk} onChange={e => setForm({ ...form, bulk: e.target.value })} />
              </Field>
              <Field label="Planned month (optional)">
                <input type="month" className={inputCls} value={form.planned_month} onChange={e => setForm({ ...form, planned_month: e.target.value })} />
              </Field>
              <Button type="submit" className="flex items-center gap-1.5 w-fit" disabled={!classId}><Plus size={14} /> Add</Button>
            </form>
          </Card>

          <Card>
            <CardHeader title="Whole school progress" />
            {!progress.length && <p className="px-5 py-5 text-sm text-ink-soft">No syllabus added yet.</p>}
            {Object.entries(byClass).map(([cls, items]) => (
              <div key={cls} className="px-5 py-3 border-t border-line-soft first:border-t-0">
                <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-1.5">Class {cls}</div>
                {items.map(p => (
                  <div key={p.subject} className="flex items-center gap-2 text-sm mb-1">
                    <span className="w-24 truncate">{p.subject}</span>
                    <div className="flex-1 h-1.5 bg-line-soft rounded-full overflow-hidden"><div className="h-full bg-transit" style={{ width: `${p.percent}%` }} /></div>
                    <span className="w-10 text-right tabular-nums font-semibold">{p.percent}%</span>
                  </div>
                ))}
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
